import { useEffect, useMemo, useState } from "react";
import { FaKey } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import { AUTH_ERROR_MESSAGES } from "../constants/auth";
import { useAuth } from "../context/AuthContext";
import usePageTitle from "../hooks/usePageTitle";
import useScrollToTopOnMessage from "../hooks/useScrollToTopOnMessage";

const readOnlyChallengeAttributes = new Set([
  "email_verified",
  "phone_number_verified",
  "sub",
]);

function attributeLabel(attributeName) {
  return attributeName
    .replace(/^custom:/, "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function initialRequiredAttributes(challenge) {
  return (challenge?.requiredAttributes || []).reduce((values, attributeName) => {
    values[attributeName] = challenge?.userAttributes?.[attributeName] || "";
    return values;
  }, {});
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { completePasswordReset, passwordChallenge } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [attributeValues, setAttributeValues] = useState(() =>
    initialRequiredAttributes(passwordChallenge),
  );
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const requiredAttributes = useMemo(
    () =>
      (passwordChallenge?.requiredAttributes || []).filter(
        (attributeName) => !readOnlyChallengeAttributes.has(attributeName),
      ),
    [passwordChallenge],
  );

  usePageTitle("Reset Password | Pargro");
  useScrollToTopOnMessage(error);

  useEffect(() => {
    if (!passwordChallenge) {
      navigate("/", {
        replace: true,
        state: {
          message: AUTH_ERROR_MESSAGES.passwordResetReauthRequired,
        },
      });
    }
  }, [navigate, passwordChallenge]);

  const updateAttribute = (attributeName) => (event) => {
    setAttributeValues((currentValues) => ({
      ...currentValues,
      [attributeName]: event.target.value,
    }));
  };

  const validateForm = () => {
    if (!newPassword || !confirmPassword) {
      return AUTH_ERROR_MESSAGES.passwordResetFieldsRequired;
    }

    if (newPassword !== confirmPassword) {
      return AUTH_ERROR_MESSAGES.passwordResetMismatch;
    }

    const missingAttribute = requiredAttributes.find(
      (attributeName) => !attributeValues[attributeName]?.trim(),
    );

    if (missingAttribute) {
      return `${attributeLabel(missingAttribute)} is required.`;
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      await completePasswordReset({
        newPassword,
        attributes: attributeValues,
      });
      navigate("/dashboard", { replace: true });
    } catch (resetError) {
      setError(resetError.message || AUTH_ERROR_MESSAGES.passwordResetFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f4f7fb]">
      <header className="flex min-h-[123px] items-center justify-center border-b border-[#d9dfe8] bg-white max-[720px]:min-h-[97px]">
        <Logo />
      </header>

      <section
        className="flex justify-center px-4 py-[50px] max-[720px]:px-3 max-[720px]:py-7"
        aria-labelledby="reset-password-title"
      >
        <form
          className="w-full max-w-[336px] rounded-[8px] bg-white px-8 pb-[31px] pt-[34px] shadow-[0_12px_30px_rgba(15,35,58,0.12)] max-[720px]:px-5 max-[720px]:pb-[23px] max-[720px]:pt-[25px]"
          onSubmit={handleSubmit}
        >
          <div className="mb-6 flex items-center justify-center gap-3">
            <FaKey className="h-5 w-5 flex-none text-[#0d73ff]" aria-hidden="true" />
            <h1
              id="reset-password-title"
              className="m-0 text-center text-2xl font-bold max-[720px]:text-xl"
            >
              Reset Password
            </h1>
          </div>

          <label
            className="mb-2.5 block text-base max-[720px]:text-sm"
            htmlFor="new-password"
          >
            New password
          </label>
          <input
            className="mb-5 h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-sm outline-none transition focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.14)]"
            id="new-password"
            name="new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />

          <label
            className="mb-2.5 block text-base max-[720px]:text-sm"
            htmlFor="confirm-password"
          >
            Confirm password
          </label>
          <input
            className="mb-5 h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-sm outline-none transition focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.14)]"
            id="confirm-password"
            name="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />

          {requiredAttributes.map((attributeName) => (
            <label className="mb-5 block" key={attributeName}>
              <span className="mb-2.5 block text-base max-[720px]:text-sm">
                {attributeLabel(attributeName)}
              </span>
              <input
                className="h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-sm outline-none transition focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.14)]"
                value={attributeValues[attributeName] || ""}
                onChange={updateAttribute(attributeName)}
              />
            </label>
          ))}

          {error ? (
            <p className="-mt-3 mb-2.5 min-h-[15px] text-[0.66rem] text-[#b42318]" role="alert">
              {error}
            </p>
          ) : null}

          <button
            className="inline-flex h-[38px] w-full items-center justify-center gap-2 rounded-md border-0 bg-[#1773f8] text-[0.9rem] font-medium text-white transition hover:bg-[#0d63dc] focus-visible:bg-[#0d63dc] disabled:cursor-not-allowed disabled:bg-[#8dbefb] active:translate-y-px"
            type="submit"
            disabled={isSubmitting}
          >
            <FaKey className="h-3.5 w-3.5" aria-hidden="true" />
            {isSubmitting ? "Saving" : "Save Password"}
          </button>
        </form>
      </section>
    </main>
  );
}

export default ResetPasswordPage;
